import React, { createContext, useContext, useState, useEffect } from "react";
import {
  useMaybeRoomContext,
  useVoiceAssistant,
} from "@livekit/components-react";
import {
  RoomEvent,
  TranscriptionSegment,
  Participant,
  TrackPublication,
  RemoteParticipant,
} from "livekit-client";
import { useConnection } from "@/hooks/use-connection";

interface WordTiming {
  word: string;
  startTime: number; // Start time of the word in seconds
  endTime: number; // End time of the word in seconds
}

interface Transcription {
  segment: TranscriptionSegment;
  participant?: Participant;
  publication?: TrackPublication;
  words: WordTiming[]; // Store word-level timestamps
}

interface AgentContextType {
  displayTranscriptions: Transcription[];
  agent?: RemoteParticipant;
}

const AgentContext = createContext<AgentContextType | undefined>(undefined);

export function AgentProvider({ children }: { children: React.ReactNode }) {
  const room = useMaybeRoomContext();
  const { shouldConnect } = useConnection();
  const { agent } = useVoiceAssistant();
  const [rawSegments, setRawSegments] = useState<{
    [id: string]: Transcription;
  }>({});
  const [displayTranscriptions, setDisplayTranscriptions] = useState<
    Transcription[]
  >([]);

  useEffect(() => {
    if (!room) {
      return;
    }

    const updateRawSegments = (
      segments: TranscriptionSegment[],
      participant?: Participant,
      publication?: TrackPublication,
    ) => {
      setRawSegments((prev) => {
        const newSegments = { ...prev };

        for (const segment of segments) {
          const baseStartTime = segment.startTime ?? Date.now() / 1000; // Use provided startTime or fallback to current time
          const wordsArray = segment.text.trim().split(/\s+/);

          let currentTime = baseStartTime;
          const wordTimings: WordTiming[] = wordsArray.map((word, index) => {
            const wordStart = currentTime;
            const wordEnd = wordStart + 0.5; // Assume each word takes ~0.5s (can be adjusted)
            currentTime = wordEnd; // Increment time for next word
            return { word, startTime: wordStart, endTime: wordEnd };
          });

          newSegments[segment.id] = {
            segment: {
              ...segment,
              startTime: baseStartTime,
              endTime: currentTime, // End of last word
            },
            participant,
            publication,
            words: wordTimings,
          };
        }

        return newSegments;
      });
    };

    room.on(RoomEvent.TranscriptionReceived, updateRawSegments);

    return () => {
      room.off(RoomEvent.TranscriptionReceived, updateRawSegments);
    };
  }, [room]);

  useEffect(() => {
    const sorted = Object.values(rawSegments).sort(
      (a, b) =>
        (a.segment.firstReceivedTime ?? 0) - (b.segment.firstReceivedTime ?? 0),
    );

    let accumulatedTime = 0;

    const mergedSorted = sorted.reduce((acc, current) => {
      if (acc.length === 0) {
        accumulatedTime = current.segment.startTime ?? accumulatedTime;
        return [
          {
            ...current,
            segment: { ...current.segment, startTime: accumulatedTime },
          },
        ];
      }

      const last = acc[acc.length - 1];

      if (
        last.participant === current.participant &&
        last.participant?.isAgent &&
        (current.segment.firstReceivedTime ?? 0) -
          (last.segment.lastReceivedTime ?? 0) <=
          1000 &&
        !last.segment.id.startsWith("status-") &&
        !current.segment.id.startsWith("status-")
      ) {
        // Merge segments from the same participant if within 1 second
        const newWords = last.words.concat(
          current.words.map((word) => ({
            ...word,
            startTime: word.startTime + accumulatedTime, // Adjust based on accumulated time
            endTime: word.endTime + accumulatedTime,
          })),
        );

        return [
          ...acc.slice(0, -1),
          {
            ...current,
            segment: {
              ...current.segment,
              text: `${last.segment.text} ${current.segment.text}`,
              startTime: last.segment.startTime, // Maintain the original start time
              endTime: newWords[newWords.length - 1].endTime, // Set new end time
            },
            words: newWords,
          },
        ];
      } else {
        accumulatedTime = current.segment.startTime ?? accumulatedTime;
        return [
          ...acc,
          {
            ...current,
            segment: { ...current.segment, startTime: accumulatedTime },
          },
        ];
      }
    }, [] as Transcription[]);

    setDisplayTranscriptions(mergedSorted);
  }, [rawSegments]);

  useEffect(() => {
    if (shouldConnect) {
      setRawSegments({});
      setDisplayTranscriptions([]);
    }
  }, [shouldConnect]);

  return (
    <AgentContext.Provider value={{ displayTranscriptions, agent }}>
      {children}
    </AgentContext.Provider>
  );
}

export function useAgent() {
  const context = useContext(AgentContext);
  if (context === undefined) {
    throw new Error("useAgent must be used within an AgentProvider");
  }
  return context;
}
